/*
 * Copyright 2026 Sony Corporation
 */
import type { Usage } from '@ips-jit/schema';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useAuth } from './auth-context';
import { UsageContext, type UsageContextValue } from './usage-context';

/** How often the usage is read while the app is open: a run adds to it as it goes. */
export const USAGE_POLL_MS = 60_000;

/** Keeps the signed-in user's monthly token usage, for the header's alert and the account menu. */
export const UsageProvider = ({ children }: { children: ReactNode }) => {
  const { api } = useAuth();
  const [usage, setUsage] = useState<Usage>();
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    api
      .getUsage()
      .then((value) => {
        if (!cancelled) {
          setUsage(value);
        }
      })
      // Usage that cannot be read shows nothing; the API still refuses work past the limit.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api, tick]);

  useEffect(() => {
    const timer = window.setInterval(refresh, USAGE_POLL_MS);
    window.addEventListener('focus', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, [refresh]);

  const value = useMemo<UsageContextValue>(() => ({ usage, refresh }), [usage, refresh]);
  return <UsageContext.Provider value={value}>{children}</UsageContext.Provider>;
};
